import React, { useState } from 'react';
import MainLayout from './layouts/MainLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import CommunityCard from '@/components/CommunityCard';
import { Link, router } from '@inertiajs/react';
import Footer from '@/components/Footer';
import { HelpCircle, Search, MapPin, ArrowRight, Heart, Calendar, DollarSign, Phone, CheckCircle, Building, Settings, CreditCard, Mic, AlertCircle } from 'lucide-react';

import heroImage from '@/assets/image2-xl@1x.webp';

export default function Home({ user, facilities }) {

  return (
    <MainLayout user={user}>
      {/* Hero Section */}
      <div className="relative h-[408px]">
        {/* Hero Background Image */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={heroImage}
            alt="Senior living community"
            className="w-full h-full object-cover "
          />
        </div>

        {/* Overlay */}
        {/* <div className="absolute inset-0 bg-black/30 z-10"></div> */}

        {/* Content */}
        <div className="relative z-20 h-full flex flex-col justify-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-left max-w-3xl">
            <h1 className="text-4xl font-extrabold text-white sm:text-5xl sm:tracking-tight lg:text-6xl mb-4 leading-tight">
              Assisted Living.
              Memory Care. <br />
              Senior Living.
            </h1>
            <p className="mt-4 text-lg text-white font-semibold">
              Find the perfect care solution for your loved ones
            </p>
          </div>

          {/* Search Box - Zillow Style */}
          <div className="mt-8 max-w-2xl w-full">
            <form onSubmit={(e) => {
              e.preventDefault();
              const location = e.target.elements.location.value;
              router.visit('/search', {
                data: { query: location }
              });
            }}>
              <div className="flex h-14 shadow-lg rounded-md overflow-hidden bg-white border-0">
                <Input
                  type="text"
                  name="location"
                  id="location"
                  placeholder="Enter an address, neighborhood, city, or ZIP code"
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full pl-4 flex-grow text-base"
                />
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary-hover text-white px-6 h-full rounded-none"
                >
                  <Search size={18} />
                </Button>
              </div>
            </form>

            <div className="mt-4 flex gap-4">
              <Button
                className="inline-flex items-center justify-center px-5 py-1.5 border border-white text-sm font-medium rounded-md bg-transparent hover:bg-white/20 text-white transition-all duration-200"
                onClick={() => {
                  // In a real implementation, this would open a modal with audio recording functionality
                  alert('This would open an audio recording interface');
                }}
              >
                <Mic size={16} className="mr-2" />
                Need Urgent Help?
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Steps - Floating Card */}
      <div className="relative z-30 mt-4 md:-mt-16 mb-0 md:-mb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg py-4 px-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-2">
              {/* Step 1 */}
              <div className="flex-1 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-primary text-white rounded-full font-bold px-3">1</span>
                <div className="relative flex-shrink-0 w-10 h-10 flex items-center justify-center bg-secondary/10 rounded-full">
                  <MapPin className="w-6 h-6 text-secondary" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold">Choose Location</h3>
                  <p className="text-[10px] text-muted-foreground">Enter your area</p>
                </div>
              </div>

              {/* Arrow 1 */}
              <div className="hidden md:block flex-shrink-0">
                <svg width="32" height="16" viewBox="0 0 60 24" fill="none">
                  <path d="M2 12L58 12M58 12L47 2M58 12L47 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* Step 2 */}
              <div className="flex-1 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-primary text-white rounded-full font-bold px-3">2</span>
                <div className="relative flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent/10 rounded-full">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold">Compare & Tour</h3>
                  <p className="text-[10px] text-muted-foreground">Schedule visits</p>
                </div>
              </div>

              {/* Arrow 2 */}
              <div className="hidden md:block flex-shrink-0">
                <svg width="32" height="16" viewBox="0 0 60 24" fill="none">
                  <path d="M2 12L58 12M58 12L47 2M58 12L47 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* Step 3 */}
              <div className="flex-1 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-primary text-white rounded-full font-bold px-3">3</span>
                <div className="relative flex-shrink-0 w-10 h-10 flex items-center justify-center bg-cta/10 rounded-full">
                  <Heart className="w-6 h-6 text-cta" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold">Find Your Home</h3>
                  <p className="text-[10px] text-muted-foreground">Secure your spot</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Communities */}
      <div className="bg-muted md:py-12 pt-0 pb-6">
        <div className="pt-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-bold text-primary">
                Homes For You in Daytona Beach, FL
              </h3>
              <p className="text-muted-foreground">
                Based on homes you recently viewed
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="p-2 rounded-full bg-white shadow-sm hover:bg-gray-50 transition-colors duration-200"
                aria-label="Previous communities"
                onClick={() => {
                  // Logic to scroll carousel left
                  const carousel = document.getElementById('communities-carousel');
                  if (carousel) {
                    carousel.scrollBy({ left: -carousel.offsetWidth, behavior: 'smooth' });
                  }
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                className="p-2 rounded-full bg-white shadow-sm hover:bg-gray-50 transition-colors duration-200"
                aria-label="Next communities"
                onClick={() => {
                  // Logic to scroll carousel right
                  const carousel = document.getElementById('communities-carousel');
                  if (carousel) {
                    carousel.scrollBy({ left: carousel.offsetWidth, behavior: 'smooth' });
                  }
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          {facilities.length > 0 ? (
            <div className="relative">
              <div
                id="communities-carousel"
                className="flex overflow-x-auto pb-4 gap-6 snap-x snap-mandatory hide-scrollbar"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {facilities.map(facility => (
                  <div
                    key={facility.id}
                    className="flex-none w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-start transform transition-all hover:-translate-y-1 duration-300"
                  >
                    <Link href={`/facilities/${facility.slug || facility.id}`}>
                      <CommunityCard community={facility} />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-soft shadow-soft">
              <p className="text-lg text-muted-foreground">No communities found</p>
            </div>
          )}

          <div className="mt-16 text-center">
            <p className="text-foreground text-lg mb-6">You're not alone in this journey. We're here every step of the way.</p>
            <Link
              href="/search"
              className="inline-flex items-center px-10 py-4 border border-transparent text-lg font-medium rounded-soft bg-cta hover:bg-cta-hover text-white shadow-soft transition-all duration-200"
            >
              Find Communities Near You
            </Link>
          </div>
        </div>
      </div>

      {/* How It Works Section - Below Hero */}
      <div className="bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">How REZY Works</h2>
            <p className="text-lg text-foreground max-w-2xl mx-auto">Three simple steps to find the perfect care solution</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Step 1 */}
            <div className="bg-background p-6 rounded-md shadow-sm flex flex-col items-center text-center h-full">
              <div className="relative mb-4 h-24 flex items-start justify-center pt-4">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-secondary/20 text-secondary">
                  <Building className="h-8 w-8" />
                </div>
                <div className="absolute top-4 right-0 -mr-2 bg-primary text-white rounded-full h-7 w-7 flex items-center justify-center font-bold">1</div>
              </div>
              <h3 className="text-lg font-semibold text-primary mb-3">Choose Your Location</h3>
              <div className="flex-grow"></div>
              <p className="text-foreground mt-auto">
                Enter your desired city or ZIP code to find communities in your area.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-background p-6 rounded-md shadow-sm flex flex-col items-center text-center h-full">
              <div className="relative mb-4 h-24 flex items-start justify-center pt-4">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-accent/20 text-accent">
                  <Settings className="h-8 w-8" />
                </div>
                <div className="absolute top-4 right-0 -mr-2 bg-primary text-white rounded-full h-7 w-7 flex items-center justify-center font-bold">2</div>
              </div>
              <h3 className="text-lg font-semibold text-primary mb-3">Select Your Amenities</h3>
              <div className="flex-grow"></div>
              <p className="text-foreground mt-auto">
                Filter by the amenities and care services that matter most to you.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-background p-6 rounded-md shadow-sm flex flex-col items-center text-center h-full">
              <div className="relative mb-4 h-24 flex items-start justify-center pt-4">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-cta/20 text-cta">
                  <CreditCard className="h-8 w-8" />
                </div>
                <div className="absolute top-4 right-0 -mr-2 bg-primary text-white rounded-full h-7 w-7 flex items-center justify-center font-bold">3</div>
              </div>
              <h3 className="text-lg font-semibold text-primary mb-3">Place Your Deposit</h3>
              <div className="flex-grow"></div>
              <p className="text-foreground mt-auto">
                Schedule tours and secure your spot with a simple deposit.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Browse by care type */}
      <div className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-primary mb-4 text-center">Browse by Care Type</h2>
          <p className="text-center text-foreground mb-12 max-w-2xl mx-auto">Understanding the right type of care is an important first step in your search</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Assisted Living */}
            <div className="relative rounded-soft overflow-hidden group cursor-pointer shadow-soft transition-all duration-300 hover:shadow-lg">
              <Link href="/search?care_type=assisted_living">
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20 z-10"></div>
                <img
                  src="https://images.unsplash.com/photo-1586105251261-72a756497a11?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                  alt="Assisted Living"
                  className="w-full h-64 object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-20">
                  <h3 className="text-xl font-bold mb-2">Assisted Living</h3>
                  <p className="text-white/90 mb-4">Support with daily activities while maintaining independence</p>
                  <span className="inline-flex items-center text-sm font-medium">
                    Explore Assisted Living
                    <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </Link>
            </div>

            {/* Memory Care */}
            <div className="relative rounded-xl overflow-hidden group cursor-pointer">
              <Link href="/search?care_type=memory_care">
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20 z-10"></div>
                <img
                  src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                  alt="Memory Care"
                  className="w-full h-64 object-cover transition-transform group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-20">
                  <h3 className="text-xl font-bold mb-2">Memory Care</h3>
                  <p className="text-white/90 mb-4">Specialized care for those with Alzheimer's and dementia</p>
                  <span className="inline-flex items-center text-sm font-medium">
                    Explore Memory Care
                    <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="bg-background py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-primary mb-4 text-center">Why Choose REZY</h2>
          <p className="text-center text-foreground mb-12 max-w-2xl mx-auto text-lg">Care you can trust, support you can feel, guidance you can depend on</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-soft shadow-soft hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-secondary/20 text-secondary mb-6">
                <Search className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-4">Thoughtful Search</h3>
              <p className="text-foreground text-lg">
                Find and compare trusted assisted living and memory care communities with search tools specifically designed for families making important care decisions.
              </p>
            </div>

            <div className="bg-white p-10 rounded-soft shadow-soft hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-accent/20 text-accent mb-6">
                <DollarSign className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-4">Transparent Pricing</h3>
              <p className="text-foreground text-lg">
                See real pricing information for all available units, with no hidden fees or surprises. We believe in complete transparency to help you make informed financial decisions.
              </p>
            </div>

            <div className="bg-white p-10 rounded-soft shadow-soft hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-cta/20 text-cta mb-6">
                <Calendar className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-semibold text-primary mb-4">Guided Tours</h3>
              <p className="text-foreground text-lg">
                Book tours directly through our platform with ease, and receive personalized guidance from our compassionate senior living experts who truly understand the emotional journey you're on.
              </p>
            </div>
          </div>

          <div className="mt-16 text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-secondary/20 text-secondary text-base font-medium mb-6">
              <Heart className="h-5 w-5 mr-2" /> Just exploring? We're here when you're ready
            </div>
            <div className="mt-6">
              <Link
                href="/search"
                className="inline-flex items-center px-10 py-4 border border-transparent text-lg font-medium rounded-soft bg-cta hover:bg-cta-hover text-white shadow-soft transition-all duration-200"
              >
                Find Communities Near You
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-secondary/10 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-primary mb-4 text-center">What Families Say About Us</h2>
          <p className="text-center text-foreground mb-12 max-w-2xl mx-auto text-lg">Real stories from families who found comfort and peace of mind through our guidance</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "REZY made finding the perfect place for my mom so much easier. The detailed information and reviews helped us make an informed decision during a difficult time.",
                author: "Jennifer K.",
                relation: "Daughter",
                image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80"
              },
              {
                quote: "We were overwhelmed with options until we found REZY. The search filters helped us narrow down exactly what we needed for my father's care. The process felt supportive.",
                author: "Michael T.",
                relation: "Son",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80"
              },
              {
                quote: "The transparent pricing and ability to schedule tours directly saved us so much time. I highly recommend REZY to anyone searching for senior living. You're not alone in this journey.",
                author: "Sarah L.",
                relation: "Granddaughter",
                image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-10 rounded-soft shadow-soft relative">
                <svg className="h-14 w-14 text-secondary/30 absolute top-8 left-8 opacity-50" fill="currentColor" viewBox="0 0 32 32">
                  <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
                </svg>
                <div className="relative z-10">
                  <p className="text-foreground mb-8 text-lg italic leading-relaxed">"{testimonial.quote}"</p>
                  <div className="flex items-center">
                    {testimonial.image && (
                      <img
                        src={testimonial.image}
                        alt={testimonial.author}
                        className="w-14 h-14 rounded-full mr-4 object-cover border-2 border-secondary/20"
                      />
                    )}
                    <div>
                      <p className="font-medium text-primary text-lg">{testimonial.author}</p>
                      <p className="text-base text-foreground">{testimonial.relation}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <p className="text-accent font-medium text-lg">Join hundreds of families who found peace of mind through our compassionate guidance</p>
          </div>
        </div>
      </div>

      <Footer />
    </MainLayout>
  );
}
